import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Avatar,
  Button,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  Collapse,
  Card,
  CardContent,
  useTheme,
  alpha,
  Container,
  Chip,
} from '@mui/material';
import {
  Dashboard as DashboardIcon,
  Assignment as PermitIcon,
  Build as MaintenanceIcon,
  Security as SafetyIcon,
  Book as StationLogIcon,
  Assessment as ReportsIcon,
  Business as PlantIcon,
  CalendarToday as PlannedWorksIcon,
  AccountCircle,
  Logout,
  ExpandLess,
  ExpandMore,
  Add as NewIcon,
  Settings as SettingsIcon,
  Help as HelpIcon,
  Notifications,
  ArrowBack,
  SwapHoriz,
  Folder as DocumentsIcon,
  Videocam as CameraIcon,
  Timeline as TimelineIcon,
  Warning as AlarmIcon,
  Visibility as ViewOnlyIcon,
} from '@mui/icons-material';

import { useAuth } from '../../hooks/useAuth';
import { useSiteContext } from '../../hooks/useSiteContext';
import { useRoleCheck } from '../../hooks/useAuth';

interface LayoutProps {
  children: React.ReactNode;
}

const drawerWidth = 280;

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { user, logout } = useAuth();
  const { currentSite } = useSiteContext();
  const { hasRole, canCreatePermits, canReceivePermits, canIssuePermits, canSuperviseWork, hasUIAccess, isViewerOnly, canViewDashboard, canViewSCADA, canViewAlarms, canViewCameras, canViewPlannedWork, canViewAllPermits, canWriteSCADATags, canClearAlarms, canManageSiteData, canAccessReports, canManageReports } = useRoleCheck();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isInSiteContext = Boolean(currentSite);

  // State for user menu
  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  
  // State for notifications
  const [notificationAnchor, setNotificationAnchor] = useState<null | HTMLElement>(null);
  const [alarmNotifications, setAlarmNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [maintenanceExpanded, setMaintenanceExpanded] = useState(false);
  const [permitExpanded, setPermitExpanded] = useState(false);

  // Fetch alarm notifications
  useEffect(() => {
    const fetchAlarmNotifications = async () => {
      if (!currentSite?.siteId) return;
      
      setLoading(true);
      try {
        // Import alarm service dynamically to avoid circular dependencies
        const { scadaAlarmsAPI } = await import('../../services/alarmService');
        
        // Fetch active alarms and recently cleared/acknowledged alarms
        const [activeAlarmsResponse, recentAlarmsResponse] = await Promise.all([
          scadaAlarmsAPI.getAll({ 
            site_id: currentSite.siteId,
            state: 'ACTIVE',
            ordering: '-triggered_at',
            page_size: 20
          }),
          scadaAlarmsAPI.getAll({ 
            site_id: currentSite.siteId,
            state__in: 'ACKNOWLEDGED,CLEARED',
            triggered_at__gte: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // Last 24 hours
            ordering: '-triggered_at',
            page_size: 10
          })
        ]);
        
        // Type assertion for API responses
        const activeAlarms = activeAlarmsResponse as { results: any[] };
        const recentAlarms = recentAlarmsResponse as { results: any[] };
        
        // Transform alarms into notification format
        const notifications = [
          ...activeAlarms.results.map((alarm: any) => ({
            id: `alarm-${alarm.id}`,
            type: 'alarm',
            priority: alarm.priority,
            title: `${alarm.tag_name || 'Unknown Tag'} - ${alarm.alarm_type}`,
            message: alarm.tag_description || `${alarm.alarm_type} alarm triggered`,
            timestamp: new Date(alarm.triggered_at),
            read: false,
            active: true,
            alarmId: alarm.id,
            tagName: alarm.tag_name
          })),
          ...recentAlarms.results.map((alarm: any) => ({
            id: `alarm-${alarm.id}`,
            type: 'alarm',
            priority: alarm.priority,
            title: `${alarm.tag_name || 'Unknown Tag'} - ${alarm.alarm_type}`,
            message: `${alarm.alarm_type} alarm ${alarm.state.toLowerCase()}`,
            timestamp: new Date(alarm.acknowledged_at || alarm.cleared_at || alarm.triggered_at),
            read: true, // Mark cleared/acknowledged as read
            active: false,
            alarmId: alarm.id,
            tagName: alarm.tag_name
          }))
        ];
        
        // Sort by timestamp (newest first)
        notifications.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
        
        setAlarmNotifications(notifications);
      } catch (error) {
        console.error('Failed to fetch alarm notifications:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAlarmNotifications();
    
    // Set up polling for active alarms every 30 seconds
    const interval = setInterval(fetchAlarmNotifications, 30000);
    
    return () => clearInterval(interval);
  }, [currentSite?.siteId]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
    setUserMenuAnchor(null);
  };

  const handleSiteSelection = () => {
    console.log('🚨 handleSiteSelection called - redirecting to dashboard!');
    console.trace('Call stack for handleSiteSelection');
    // clearSiteSelection(); // This line was removed from the new_code, so it's removed here.
    navigate('/dashboard');
  };

  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };

  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };

  const handleProfileClick = () => {
    handleUserMenuClose();
    navigate('/user-settings');
  };

  const handleSettingsClick = () => {
    handleUserMenuClose();
    navigate('/user-settings?tab=1');
  };

  const handleHelpClick = () => {
    handleUserMenuClose();
    navigate('/user-settings?tab=2');
  };

  const handleNotificationOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchor(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchor(null);
  };

  const handleNotificationClick = (notificationId: string) => {
    const notification = alarmNotifications.find(n => n.id === notificationId);
    if (notification) {
      // Mark as read
      setAlarmNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId ? { ...notif, read: true } : notif
        )
      );
      
      // Navigate to alarm management if it's an alarm
      if (notification.type === 'alarm') {
        navigate('/alarms');
      }
    }
    handleNotificationClose();
  };

  const markAllAsRead = () => {
    setAlarmNotifications(prev => 
      prev.map(notif => ({ ...notif, read: true }))
    );
  };

  // Calculate badge counts
  const activeAlarmCount = alarmNotifications.filter(n => n.active).length;
  const unreadCount = alarmNotifications.filter(n => !n.read).length;
  
  // Badge color logic: red for active alarms, grey for unread non-active
  const getBadgeColor = () => {
    if (activeAlarmCount > 0) return 'error'; // Red for active alarms
    if (unreadCount > 0) return 'default';    // Grey for unread cleared/acknowledged
    return 'default';
  };
  
  const getBadgeCount = () => {
    return activeAlarmCount > 0 ? activeAlarmCount : unreadCount;
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  // Filter navigation items based on user permissions and backend configuration
  const getFilteredNavigationItems = () => {
    const baseItems = [
      {
        title: 'Dashboard',
        path: '/dashboard',
        icon: <DashboardIcon />,
        active: isActive('/dashboard'),
        show: canViewDashboard(),
        order: 1
      },
      {
        title: 'Plant Information',
        path: '/plant-information',
        icon: <PlantIcon />,
        active: isActive('/plant-information'),
        show: hasUIAccess('plant_information'),
        order: 2
      },
      {
        title: 'SCADA Historian',
        path: '/historian',
        icon: <TimelineIcon />,
        active: isActive('/historian'),
        show: canViewSCADA(),
        order: 3
      },
      {
        title: 'Alarm Management',
        path: '/alarms',
        icon: <AlarmIcon />,
        active: isActive('/alarms'),
        show: canViewAlarms(),
        order: 4
      },
      {
        title: 'Documents',
        path: '/documents',
        icon: <DocumentsIcon />,
        active: isActive('/documents'),
        show: hasUIAccess('documents'),
        order: 5
      },
      {
        title: 'Planned Works',
        path: '/planned-works',
        icon: <PlannedWorksIcon />,
        active: isActive('/planned-works'),
        show: canViewPlannedWork(),
        order: 6
      },
      {
        title: 'Maintenance',
        path: '/maintenance',
        icon: <MaintenanceIcon />,
        active: isActive('/maintenance'),
        show: hasUIAccess('maintenance'),
        order: 7
      },
      {
        title: 'Permits',
        path: '/permits',
        icon: <PermitIcon />,
        active: isActive('/permits'),
        show: canViewAllPermits(),
        order: 8
      },
      {
        title: 'Station Log',
        path: '/station-log',
        icon: <StationLogIcon />,
        active: isActive('/station-log'),
        show: hasUIAccess('station_log'),
        order: 9
      },
      {
        title: 'Safety',
        path: '/safety',
        icon: <SafetyIcon />,
        active: isActive('/safety'),
        show: hasUIAccess('safety'),
        order: 10
      },
      {
        title: 'Cameras',
        path: '/cameras',
        icon: <CameraIcon />,
        active: isActive('/cameras'),
        show: canViewCameras(),
        order: 11
      },
      {
        title: 'Reports',
        path: '/reports',
        icon: <ReportsIcon />,
        active: isActive('/reports'),
        show: canAccessReports(),
        order: 12
      }
    ];

    // Filter items based on permissions and sort by order
    return baseItems
      .filter(item => item.show)
      .sort((a, b) => a.order - b.order);
  };

  const navigationItems = getFilteredNavigationItems();

  if (!isInSiteContext) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
        {/* Header for non-site context */}
        {user && (
          <AppBar position="static" elevation={0}>
            <Toolbar>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Re-GenOps
              </Typography>
              <IconButton color="inherit" onClick={handleUserMenuOpen}>
                <AccountCircle />
              </IconButton>
              <Menu
                anchorEl={userMenuAnchor}
                open={Boolean(userMenuAnchor)}
                onClose={handleUserMenuClose}
              >
                <MenuItem onClick={handleLogout}>
                  <ListItemIcon>
                    <Logout fontSize="small" />
                  </ListItemIcon>
                  Logout
                </MenuItem>
              </Menu>
            </Toolbar>
          </AppBar>
        )}
        
        <Container maxWidth="xl" sx={{ flex: 1, py: 4 }}>
          {children}
        </Container>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex' }}>
      {/* Navigation Drawer */}
      <Drawer
        variant="permanent"
        sx={{
          width: drawerWidth,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: drawerWidth,
            boxSizing: 'border-box',
            bgcolor: theme.palette.primary.main,
            color: 'white',
            borderRight: 'none',
          },
        }}
      >
        {/* Logo Section */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 3,
            bgcolor: alpha(theme.palette.common.white, 0.1),
            borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          }}
        >
          <Box
            sx={{
              bgcolor: 'rgba(255, 255, 255, 0.9)',
              borderRadius: 2,
              p: 1.5,
              display: 'flex',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
            }}
          >
            <img
              src="/ReGenOps_alpha.png"
              alt="Re-GenOps"
              style={{ height: '32px', width: 'auto' }}
            />
          </Box>
        </Box>

        {/* Site Information Card */}
        <Box sx={{ px: 2, py: 1 }}>
                  <Card
          sx={{
            bgcolor: alpha(theme.palette.common.white, 0.1),
            color: 'white',
            border: `1px solid ${alpha(theme.palette.common.white, 0.2)}`,
          }}
          >
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 600, mb: 0.5 }}>
                {currentSite.siteName}
              </Typography>
              
              {/* Asset Owner Logo or Name */}
              {currentSite.assetOwnerLogoUrl ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                  <img
                    src={currentSite.assetOwnerLogoUrl}
                    alt={currentSite.assetOwnerName || 'Asset Owner'}
                    style={{
                      maxHeight: '40px',
                      maxWidth: '100%',
                      objectFit: 'contain',
                      // Removed filter to show original logo colors
                    }}
                  />
                </Box>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.875rem', textAlign: 'center' }}>
                  {currentSite.assetOwnerName}
                </Typography>
              )}
              
              {/* Site Actions */}
              <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                <Button
                  size="small"
                  startIcon={<SwapHoriz />}
                  onClick={handleSiteSelection}
                  sx={{
                    color: theme.palette.secondary.light,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    minWidth: 'auto',
                  }}
                >
                  Change Site
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>

        <Divider sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), mx: 2, my: 1 }} />

        {/* Navigation Items */}
        <List sx={{ px: 1, flex: 1 }}>
          {navigationItems.map((item) => (
            <ListItem key={item.title} disablePadding>
              <ListItemButton
                component={Link}
                to={item.path}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  mx: 1,
                  bgcolor: item.active ? alpha(theme.palette.common.white, 0.15) : 'transparent',
                  '&:hover': {
                    bgcolor: alpha(theme.palette.common.white, 0.1),
                  },
                  '&.Mui-selected': {
                    bgcolor: alpha(theme.palette.common.white, 0.15),
                  },
                }}
                selected={item.active}
              >
                <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.title}
                  primaryTypographyProps={{
                    fontSize: '0.875rem',
                    fontWeight: item.active ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>

        <Divider sx={{ bgcolor: alpha(theme.palette.common.white, 0.2), mx: 2 }} />

        {/* User Section */}
        <Box sx={{ p: 2 }}>
          <ListItemButton
            onClick={handleUserMenuOpen}
            sx={{
              borderRadius: 2,
              bgcolor: alpha(theme.palette.common.white, 0.05),
              '&:hover': {
                bgcolor: alpha(theme.palette.common.white, 0.1),
              },
            }}
          >
            <ListItemIcon>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: theme.palette.secondary.main,
                  fontSize: '0.875rem',
                }}
              >
                {user?.full_name?.charAt(0) || user?.username?.charAt(0) || 'U'}
              </Avatar>
            </ListItemIcon>
            <ListItemText
              primary={user?.full_name || user?.username}
              secondary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontSize: '0.75rem',
                      color: alpha(theme.palette.common.white, 0.7),
                    }}
                  >
                    {user?.role}
                  </Typography>
                  {isViewerOnly() && (
                    <Chip
                      icon={<ViewOnlyIcon />}
                      label="View Only"
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: '0.65rem',
                        bgcolor: alpha(theme.palette.warning.main, 0.2),
                        color: theme.palette.warning.light,
                        '& .MuiChip-icon': {
                          fontSize: '0.75rem',
                        },
                      }}
                    />
                  )}
                </Box>
              }
              primaryTypographyProps={{
                fontSize: '0.875rem',
                fontWeight: 500,
                color: 'white',
              }}
              secondaryTypographyProps={{
                component: 'div',
              }}
            />
          </ListItemButton>

          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            PaperProps={{
              sx: { width: 200 },
            }}
          >
            <MenuItem onClick={handleProfileClick}>
              <ListItemIcon>
                <AccountCircle fontSize="small" />
              </ListItemIcon>
              Profile
            </MenuItem>
            <MenuItem onClick={handleSettingsClick}>
              <ListItemIcon>
                <SettingsIcon fontSize="small" />
              </ListItemIcon>
              Settings
            </MenuItem>
            <MenuItem onClick={handleHelpClick}>
              <ListItemIcon>
                <HelpIcon fontSize="small" />
              </ListItemIcon>
              Help
            </MenuItem>
            <Divider />
            <MenuItem onClick={handleLogout}>
              <ListItemIcon>
                <Logout fontSize="small" />
              </ListItemIcon>
              Logout
            </MenuItem>
          </Menu>
        </Box>
      </Drawer>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          bgcolor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        {/* Top App Bar */}
        <AppBar
          position="static"
          elevation={0}
          sx={{
            bgcolor: 'white',
            color: theme.palette.text.primary,
            borderBottom: `1px solid ${theme.palette.divider}`,
          }}
        >
          <Toolbar sx={{ px: 4 }}>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1, color: theme.palette.primary.main }}>
              {getPageTitle(location.pathname)}
            </Typography>
            
            {/* Top bar actions */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <IconButton color="inherit" onClick={handleNotificationOpen}>
                <Badge badgeContent={getBadgeCount()} color={getBadgeColor()}>
                  <Notifications />
                </Badge>
              </IconButton>
              
              {/* Notification Menu */}
              <Menu
                anchorEl={notificationAnchor}
                open={Boolean(notificationAnchor)}
                onClose={handleNotificationClose}
                PaperProps={{
                  sx: { 
                    width: 400,
                    maxHeight: 500
                  },
                }}
              >
                <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h6">
                      Alarm Notifications
                      {activeAlarmCount > 0 && (
                        <Chip 
                          label={`${activeAlarmCount} Active`} 
                          color="error" 
                          size="small" 
                          sx={{ ml: 1 }} 
                        />
                      )}
                    </Typography>
                    {unreadCount > 0 && (
                      <Button size="small" onClick={markAllAsRead}>
                        Mark all as read
                      </Button>
                    )}
                  </Box>
                </Box>
                
                <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
                  {loading ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">Loading alarms...</Typography>
                    </Box>
                  ) : alarmNotifications.length === 0 ? (
                    <Box sx={{ p: 3, textAlign: 'center' }}>
                      <Typography color="text.secondary">No recent alarms</Typography>
                    </Box>
                  ) : (
                    alarmNotifications.map((notification) => (
                      <MenuItem
                        key={notification.id}
                        onClick={() => handleNotificationClick(notification.id)}
                        sx={{
                          borderBottom: 1,
                          borderColor: 'divider',
                          bgcolor: notification.read ? 'transparent' : (notification.active ? 'error.light' : 'action.hover'),
                          opacity: notification.active ? 1 : 0.8,
                          '&:hover': {
                            bgcolor: 'action.selected',
                          },
                        }}
                      >
                        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography 
                              variant="subtitle2" 
                              sx={{ 
                                fontWeight: notification.read ? 400 : 600,
                                color: notification.active ? 'error.main' : 'text.primary'
                              }}
                            >
                              {notification.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {notification.timestamp.toLocaleTimeString()}
                            </Typography>
                          </Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            {notification.message}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip
                              label={notification.active ? 'ACTIVE' : 'CLEARED'}
                              size="small"
                              color={notification.active ? 'error' : 'default'}
                              variant={notification.active ? 'filled' : 'outlined'}
                            />
                            <Chip
                              label={notification.priority}
                              size="small"
                              color={
                                notification.priority === 'CRITICAL' ? 'error' :
                                notification.priority === 'HIGH' ? 'warning' :
                                notification.priority === 'MEDIUM' ? 'info' : 'default'
                              }
                              variant="outlined"
                            />
                          </Box>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Box>
              </Menu>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Page Content */}
        <Container maxWidth="xl" sx={{ p: 4 }}>
          {children}
        </Container>
      </Box>
    </Box>
  );
};

// Helper function to get page title based on route
const getPageTitle = (pathname: string): string => {
  if (pathname.startsWith('/maintenance')) {
    if (pathname === '/maintenance/work-orders') return 'Maintenance Work Orders';
    if (pathname === '/maintenance/schedules') return 'Maintenance Schedules';
    if (pathname === '/maintenance/routes') return 'Maintenance Routes';
    if (pathname === '/maintenance/standard-tasks') return 'Standard Tasks';
    if (pathname === '/maintenance/records') return 'Maintenance Records';
    return 'Maintenance Dashboard';
  }
  
  if (pathname.startsWith('/permits')) {
    if (pathname === '/permits/new') return 'Create New Permit';
    if (pathname === '/permits/comprehensive') return 'Comprehensive Permit Form';
    if (pathname.match(/\/permits\/\d+/)) return 'Permit Details';
    return 'Permit Management';
  }
  
  switch (pathname) {
    case '/dashboard': return 'Dashboard';
    case '/plant-information': return 'Plant Information';
    case '/documents': return 'Documents';
    case '/planned-works': return 'Planned Works';
    case '/station-log': return 'Station Log';
    case '/safety': return 'Safety Management';
    case '/reports': return 'Reports & Analytics';
    default: return 'Re-GenOps';
  }
};

export default Layout; 